<template>
  <v-navigation-drawer
    :model-value="modelValue"
    location="right"
    temporary
    width="480"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="ncrm-em-plan-drawer__header">
      <span class="ncrm-em-plan-drawer__title">
        {{ isEdit ? $t("ncrm.emailMarketing.planner.editTitle") : $t("ncrm.emailMarketing.planner.createTitle") }}
      </span>
      <v-btn icon variant="text" size="small" @click="$emit('update:modelValue', false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-divider />

    <div class="ncrm-em-plan-drawer__body">
      <v-alert v-if="errorMessage" type="error" class="mb-3">
        {{ errorMessage }}
      </v-alert>

      <v-text-field
        v-model="form.title"
        :label="$t('ncrm.emailMarketing.planner.fields.title')"
        class="mb-3"
      />
      <v-combobox
        v-model="form.category"
        :items="categories"
        :label="$t('ncrm.emailMarketing.planner.fields.category')"
        clearable
        class="mb-3"
      />
      <v-select
        v-model="form.status"
        :items="statusItems"
        :label="$t('ncrm.emailMarketing.planner.fields.status')"
        class="mb-3"
      />
      <NcrmDateTimeField
        v-model="form.plannedSendAt"
        :label="$t('ncrm.emailMarketing.planner.fields.plannedSendAt')"
        variant="outlined"
        density="comfortable"
        clearable
        class="mb-3"
      />
      <v-text-field
        v-model="form.topic"
        :label="$t('ncrm.emailMarketing.planner.fields.topic')"
        class="mb-3"
      />
      <v-text-field
        v-model="form.audienceSegment"
        :label="$t('ncrm.emailMarketing.planner.fields.audienceSegment')"
        class="mb-3"
      />
      <v-text-field
        v-model="form.objective"
        :label="$t('ncrm.emailMarketing.planner.fields.objective')"
        class="mb-3"
      />
      <v-text-field
        v-model="form.cta"
        :label="$t('ncrm.emailMarketing.planner.fields.cta')"
        class="mb-3"
      />
      <v-select
        v-model="form.mailjetCampaignId"
        :items="campaignItems"
        :label="$t('ncrm.emailMarketing.planner.fields.campaign')"
        clearable
        class="mb-3"
      />
      <v-textarea
        v-model="form.notes"
        :label="$t('ncrm.emailMarketing.planner.fields.notes')"
        rows="3"
        auto-grow
        class="mb-3"
      />
    </div>

    <template #append>
      <v-divider />
      <div class="ncrm-em-plan-drawer__footer">
        <v-btn variant="text" @click="$emit('update:modelValue', false)">
          {{ $t("ncrm.emailMarketing.planner.cancel") }}
        </v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!form.title" @click="submit">
          {{ $t("ncrm.emailMarketing.planner.save") }}
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";
import { EMAIL_MARKETING_PLAN_STATUSES } from "@/Utile/ncrmEmailMarketing.mjs";

const emptyForm = () => ({
  title: "",
  category: null,
  status: "draft",
  plannedSendAt: "",
  topic: "",
  audienceSegment: "",
  objective: "",
  cta: "",
  notes: "",
  mailjetCampaignId: null,
});

const toLocalInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default {
  name: "NcrmEmailMarketingPlanDrawer",
  components: { NcrmDateTimeField },
  props: {
    modelValue: { type: Boolean, default: false },
    plan: { type: Object, default: null },
    categories: { type: Array, default: () => [] },
    campaigns: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    errorMessage: { type: String, default: "" },
  },
  emits: ["update:modelValue", "save"],
  data() {
    return {
      form: emptyForm(),
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.plan?.id);
    },
    statusItems() {
      return EMAIL_MARKETING_PLAN_STATUSES.map((status) => ({
        value: status,
        title: this.$t(`ncrm.emailMarketing.planner.statuses.${status}`),
      }));
    },
    campaignItems() {
      return this.campaigns.map((campaign) => ({
        value: campaign.id,
        title: campaign.subject || `#${campaign.mailjet_campaign_id}`,
      }));
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    resetForm() {
      if (!this.plan) {
        this.form = emptyForm();
        return;
      }
      this.form = {
        title: this.plan.title || "",
        category: this.plan.category || null,
        status: this.plan.status || "draft",
        plannedSendAt: toLocalInputValue(this.plan.planned_send_at),
        topic: this.plan.topic || "",
        audienceSegment: this.plan.audience_segment || "",
        objective: this.plan.objective || "",
        cta: this.plan.cta || "",
        notes: this.plan.notes || "",
        mailjetCampaignId: this.plan.mailjet_campaign_id || null,
      };
    },
    submit() {
      this.$emit("save", {
        title: this.form.title,
        category: this.form.category || null,
        status: this.form.status,
        plannedSendAt: this.form.plannedSendAt || null,
        topic: this.form.topic || null,
        audienceSegment: this.form.audienceSegment || null,
        objective: this.form.objective || null,
        cta: this.form.cta || null,
        notes: this.form.notes || null,
        mailjetCampaignId: this.form.mailjetCampaignId || null,
      });
    },
  },
};
</script>

<style scoped>
.ncrm-em-plan-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
}

.ncrm-em-plan-drawer__title {
  font-weight: 600;
  color: var(--ncrm-color-text-strong);
}

.ncrm-em-plan-drawer__body {
  padding: 16px;
}

.ncrm-em-plan-drawer__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}
</style>
